import { notifyError } from "@/lib/notify-error";
import { Fragment, useState } from "react";
import { useMutation } from "convex/react";
import { Check, Pencil, Trash2, X } from "lucide-react";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { IconButton } from "@/components/ui/icon-button";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { getUserFacingErrorMessage } from "@/lib/user-facing-errors";
import {
  formatDurationDays,
  formatScheduleDate,
  formatSchedulePercent,
  milestoneDateBounds,
  type FieldPilotScheduleRow,
} from "@/features/field-pilot/schedule-format";
import {
  FIELD_PILOT_COLUMN_BAND,
  FIELD_PILOT_ROW_CELL,
  FieldPilotTreeCellContent,
  fieldPilotRowClasses,
  fieldPilotRowIndentRem,
} from "@/features/field-pilot/row-hierarchy";
import { ScheduleProgressBar } from "@/features/field-pilot/schedule-visualizations";
import { StatusDot, StatusOptionLabel } from "@/features/field-pilot/status-dot";
import { useCanEdit } from "@/lib/capabilities";

export interface FieldPilotSubTaskMilestone {
  statusId: Id<"fieldPilotStatuses">;
  name: string | null;
  colorHex: string | null;
  dueDateKey: string | null;
  reached: boolean;
  fraction: number;
}

/** One entry per active status, in pipeline order, powering the secondary drill-down. */
export interface FieldPilotSubTaskStatusLineEntry {
  statusId: Id<"fieldPilotStatuses">;
  name: string | null;
  colorHex: string | null;
  rank: number;
  dueDateKey: string | null;
  reached: boolean;
  atOrPastCount: number;
  memberCount: number;
}

/** The schedule metrics every child row renders, whatever kind of child it is. */
export interface FieldPilotChildRowMetrics {
  itemCount: number;
  startDateKey: string | null;
  finishDateKey: string | null;
  durationDays: number | null;
  actualFraction: number;
  expectedFraction: number;
  finishVarianceDays: number | null;
  isBehind: boolean;
  hasSchedule: boolean;
}

/**
 * The items on a task that are in no sub-task. The backend rollup already weights this bucket
 * alongside the real sub-tasks, so it is shown as their peer — otherwise the task's percentage
 * cannot be reconciled against the rows beneath it (CON-405). It is derived, not stored: no id,
 * no milestones, nothing to edit.
 */
export interface FieldPilotUngroupedRow extends FieldPilotChildRowMetrics {
  total?: number;
}

export interface FieldPilotSubTaskRow extends FieldPilotChildRowMetrics {
  subTaskId: Id<"fieldPilotSubTasks">;
  name: string;
  displayOrder: number;
  /** Total measured/count value for the sub-task, used for "Total" displays. */
  total?: number;
  /** Raw explicit start override; `null` when the sub-task inherits the parent task's start. */
  startDateKeyOverride: string | null;
  /** Which model drives progress: pipeline-status completion, or dated milestones. */
  progressMethod?: "completion_statuses" | "milestones";
  /** Dated checkpoints (date + cumulative fraction) used to interpolate `expectedFraction`. */
  plannedCheckpoints?: { dateKey: string; fraction: number }[];
  /** The sub-task's current pipeline stage (highest fully-reached status), or null. */
  currentStatus: {
    statusId: Id<"fieldPilotStatuses">;
    name: string | null;
    colorHex: string | null;
  } | null;
  /** Every active status in pipeline order, for the per-status drill-down. */
  statusLine: FieldPilotSubTaskStatusLineEntry[];
  milestones: FieldPilotSubTaskMilestone[];
  nextMilestone: {
    statusId: Id<"fieldPilotStatuses">;
    name: string | null;
    dueDateKey: string | null;
  } | null;
}

/** A schedule row that also carries its rolled-up sub-tasks (from `getScheduleByJob`). */
export interface FieldPilotScheduleRowWithSubTasks extends FieldPilotScheduleRow {
  hasSubTasks: boolean;
  subTasks: FieldPilotSubTaskRow[];
  /** The task's ungrouped remainder, or null when every item is grouped (or there are none). */
  ungrouped?: FieldPilotUngroupedRow | null;
}

/**
 * Describes the host table the sub-task rows are slotting into so each metric lands in the
 * matching column. Both call sites pass the same `layout` prop name (a discriminated union).
 */
export type SubTaskRowsLayout =
  | { variant: "progress"; showScheduleColumns: boolean }
  | { variant: "schedule" };

const PIPELINE_FALLBACK_COLOR = "#94A3B8";
const NEW_MILESTONE_PLACEHOLDER = "+ Add milestone";

/** Single-item sub-tasks are item schedules: a distinct kind of child, not a distinct level. */
export function isSoloItemSchedule(subTask: Pick<FieldPilotSubTaskRow, "itemCount">): boolean {
  return subTask.itemCount === 1;
}

/**
 * Split a task's children into the two kinds it can hold, and report whether it has any at all.
 *
 * Both tables ask this rather than deciding for themselves. Gating only the grouped sub-tasks —
 * the previous rule — left item schedules permanently visible under a collapsed task and denied a
 * toggle to any task holding nothing but items. A task's toggle owns *every* child it has (CON-405).
 */
export function splitTaskChildRows<T extends Pick<FieldPilotSubTaskRow, "itemCount">>(
  subTasks: T[],
  ungrouped?: FieldPilotUngroupedRow | null
): { items: T[]; groups: T[]; hasChildRows: boolean } {
  return {
    items: subTasks.filter(isSoloItemSchedule),
    groups: subTasks.filter((subTask) => !isSoloItemSchedule(subTask)),
    // The ungrouped bucket is a child too, so it counts toward whether the task can expand.
    hasChildRows: subTasks.length > 0 || Boolean(ungrouped),
  };
}

/** Total host columns, used for the milestone drill-down's full-width `colSpan`. */
function getColumnCount(layout: SubTaskRowsLayout): number {
  // Tag + Task Name + Start + Finish + Duration + Expected + Actual + On Track + row actions
  if (layout.variant === "schedule") return 9;
  // actions + Task + Total + Status Breakdown (+ 6 schedule columns when shown)
  return 4 + (layout.showScheduleColumns ? 6 : 0);
}

/**
 * Compact pipeline of dots — one per ASSIGNED status (a status-line entry with a due date) —
 * ordered by rank and filled per `reached`. The starting stage and undated statuses are not
 * assigned, so they are not shown; a sub-task with no milestones renders nothing.
 */
function MiniPipeline({ statusLine }: { statusLine: FieldPilotSubTaskStatusLineEntry[] }) {
  const assigned = statusLine
    .filter((entry) => entry.dueDateKey)
    .sort((a, b) => a.rank - b.rank);
  if (assigned.length === 0) {
    return null;
  }
  return (
    <span className="flex shrink-0 items-center" aria-hidden="true">
      {assigned.map((entry, index) => {
        const color = entry.colorHex ?? PIPELINE_FALLBACK_COLOR;
        return (
          <Fragment key={String(entry.statusId)}>
            {index > 0 ? <span className="h-px w-2.5 bg-border" /> : null}
            <span
              className="size-2.5 rounded-full border"
              style={
                entry.reached
                  ? { backgroundColor: color, borderColor: color }
                  : { borderColor: color, backgroundColor: "transparent" }
              }
              title={`${entry.name ?? "Status"}${entry.reached ? " · reached" : ""}`}
            />
          </Fragment>
        );
      })}
    </span>
  );
}

/** Current-stage chip (mini pipeline + the current status name) reused across both variants. */
function CurrentStageCell({ subTask }: { subTask: FieldPilotSubTaskRow }) {
  return (
    <div className="flex items-center gap-2">
      <MiniPipeline statusLine={subTask.statusLine ?? []} />
      <span className="truncate text-xs text-muted-foreground">
        {subTask.currentStatus?.name ?? "—"}
      </span>
    </div>
  );
}

function DeleteEmptySubTaskButton({
  subTask,
  onDeleteSubTask,
}: {
  subTask: FieldPilotSubTaskRow;
  onDeleteSubTask?: (subTaskId: Id<"fieldPilotSubTasks">) => void;
}) {
  const canEdit = useCanEdit();
  if (!canEdit || subTask.itemCount !== 0 || !onDeleteSubTask) return null;

  const milestoneCount = subTask.milestones.length;
  const button = (
    <IconButton
      label={`Delete empty sub-task ${subTask.name}`}
      icon={Trash2}
      iconClassName="size-3.5"
      className="shrink-0 text-destructive hover:text-destructive"
      onClick={milestoneCount === 0 ? () => onDeleteSubTask(subTask.subTaskId) : undefined}
    />
  );
  if (milestoneCount === 0) {
    return button;
  }

  return (
    <ConfirmationDialog
      title="Delete sub-task?"
      description={`${subTask.name} has ${milestoneCount} milestone${milestoneCount === 1 ? "" : "s"}. Deleting it will permanently remove the sub-task and its milestone${milestoneCount === 1 ? "" : "s"}.`}
      confirmLabel="Delete sub-task"
      confirmVariant="destructive"
      onConfirm={() => onDeleteSubTask(subTask.subTaskId)}
    >
      {button}
    </ConfirmationDialog>
  );
}

/**
 * The tree cell for a sub-task / item schedule: depth chrome and kind badge from the shared
 * hierarchy helpers, then the name and its inline edit affordance. The milestone toggle lives here
 * too — the name column is the only column allowed to hold an expand control.
 */
function SubTaskNameCell({
  subTask,
  onEditSubTask,
  isExpanded,
  onToggle,
  trailing,
}: {
  subTask: FieldPilotSubTaskRow;
  onEditSubTask: (subTaskId: Id<"fieldPilotSubTasks">) => void;
  isExpanded: boolean;
  onToggle?: () => void;
  trailing?: React.ReactNode;
}) {
  const isSolo = isSoloItemSchedule(subTask);
  const canEdit = useCanEdit();
  return (
    <FieldPilotTreeCellContent
      kind="sub-task"
      isExpanded={isExpanded}
      onToggle={onToggle}
      toggleLabel={isExpanded ? "Collapse milestones" : "Expand milestones"}
      trailing={trailing}
    >
      <div className="flex min-w-0 items-center gap-0.5">
        <span className="min-w-0 truncate text-sm font-medium" title={subTask.name}>
          {subTask.name}
        </span>
        {canEdit ? (
          <IconButton
            label={isSolo ? `Edit item schedule ${subTask.name}` : `Edit sub-task ${subTask.name}`}
            icon={Pencil}
            size="xs"
            iconClassName="size-3.5"
            className="shrink-0"
            onClick={() => onEditSubTask(subTask.subTaskId)}
          />
        ) : null}
      </div>
    </FieldPilotTreeCellContent>
  );
}

/**
 * Full-width milestone drill-down. Lists only dated milestones whose status sits past the
 * starting stage (rank > 0), plus an "Add milestone" control for the remaining eligible
 * statuses. The starting stage (rank 0) is never a milestone.
 */
function MilestoneDrillDown({
  subTask,
  colSpan,
}: {
  subTask: FieldPilotSubTaskRow;
  colSpan: number;
}) {
  const subTaskId = subTask.subTaskId;
  const statusLine = subTask.statusLine ?? [];
  const upsertMilestone = useMutation(api.fieldPilot.subTasks.upsertMilestone);
  const removeMilestone = useMutation(api.fieldPilot.subTasks.removeMilestone);
  const setStart = useMutation(api.fieldPilot.subTasks.setStart);
  const [addStatusId, setAddStatusId] = useState("");
  // Reviewers see the milestone schedule as text; setting dates is a project write.
  const canEdit = useCanEdit();

  const eligible = statusLine.filter((entry) => entry.rank > 0);
  const milestones = eligible.filter((entry) => entry.dueDateKey);
  const addable = eligible.filter((entry) => !entry.dueDateKey);
  // Every dated milestone in the pipeline, used to constrain pickers so dates can't be set out
  // of pipeline order (the backend rejects out-of-order dates; these bounds prevent it up front).
  const datedForBounds = statusLine
    .filter((entry) => entry.dueDateKey)
    .map((entry) => ({ rank: entry.rank, dueDateKey: entry.dueDateKey }));
  // Default the add row to the next un-dated status so the common case is a single date pick.
  const effectiveAddStatusId =
    addStatusId && addable.some((entry) => String(entry.statusId) === addStatusId)
      ? addStatusId
      : String(addable[0]?.statusId ?? "");
  const addRank = effectiveAddStatusId
    ? statusLine.find((entry) => String(entry.statusId) === effectiveAddStatusId)?.rank
    : undefined;
  const addBounds =
    addRank === undefined ? {} : milestoneDateBounds(datedForBounds, addRank);

  const handleSet = (statusId: Id<"fieldPilotStatuses">, dueDateKey: string) => {
    upsertMilestone({ subTaskId, statusId, dueDateKey }).catch((error) =>
      notifyError(error, getUserFacingErrorMessage(error, "Failed to set milestone"))
    );
  };
  const handleRemove = (statusId: Id<"fieldPilotStatuses">) => {
    removeMilestone({ subTaskId, statusId }).catch((error) =>
      notifyError(error, getUserFacingErrorMessage(error, "Failed to remove milestone"))
    );
  };

  // Start is owner-driven: the picker reflects the explicit override; clearing it inherits the
  // parent task's start. The latest dated milestone (falling back to the cached finish) caps the
  // start so an invalid (after the final milestone) date can't be picked — the backend also rejects it.
  const isInheritingStart = subTask.startDateKeyOverride === null;
  const latestMilestoneDateKey = datedForBounds.reduce<string | undefined>(
    (latest, milestone) =>
      milestone.dueDateKey && (latest === undefined || milestone.dueDateKey > latest)
        ? milestone.dueDateKey
        : latest,
    undefined
  );
  const startMax = latestMilestoneDateKey ?? subTask.finishDateKey ?? undefined;
  const handleSetStart = (value: string) => {
    setStart({ subTaskId, startDateKey: value ? value : null }).catch((error) =>
      notifyError(error, getUserFacingErrorMessage(error, "Failed to set start date"))
    );
  };

  return (
    <TableRow className={fieldPilotRowClasses({ kind: "detail" })}>
      <TableCell colSpan={colSpan} className="p-0">
        {/* Two columns: Start + Add on the left, the milestone list on the right — compact, but
            keeps the familiar stacked-rows feel and saves vertical room versus one tall column. */}
        <div
          className="flex flex-col gap-4 border-s-2 border-s-border py-2.5 pe-4 text-xs sm:flex-row sm:gap-8"
          style={{ marginInlineStart: `${fieldPilotRowIndentRem("detail")}rem`, paddingInlineStart: "0.75rem" }}
        >
          {/* LEFT: start date + add milestone */}
          <div className="flex w-full shrink-0 flex-col gap-3 sm:w-[260px]">
            <div className="space-y-1">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Start
              </p>
              <div className="flex items-center gap-2">
                {canEdit ? (
                  <DatePicker
                    className="w-[150px]"
                    size="sm"
                    value={subTask.startDateKeyOverride ?? ""}
                    max={startMax}
                    placeholder={formatScheduleDate(subTask.startDateKey)}
                    onChange={handleSetStart}
                    aria-label="Sub-task start date"
                  />
                ) : (
                  <span className="tabular-nums">{formatScheduleDate(subTask.startDateKey)}</span>
                )}
                {isInheritingStart ? (
                  <span className="text-[11px] text-muted-foreground">inherited</span>
                ) : null}
              </div>
            </div>
            {canEdit && addable.length > 0 ? (
              <div className="space-y-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Add milestone
                </p>
                <div className="flex items-center gap-2">
                  <Select value={effectiveAddStatusId} onValueChange={setAddStatusId}>
                    <SelectTrigger size="sm" className="w-[150px]" aria-label="Milestone status">
                      <SelectValue placeholder={NEW_MILESTONE_PLACEHOLDER} />
                    </SelectTrigger>
                    <SelectContent className="z-[200]">
                      {addable.map((entry) => (
                        <SelectItem key={String(entry.statusId)} value={String(entry.statusId)}>
                          <StatusOptionLabel colorHex={entry.colorHex} name={entry.name ?? "Status"} />
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <DatePicker
                    className="w-[150px]"
                    size="sm"
                    value=""
                    disabled={!effectiveAddStatusId}
                    min={addBounds.min}
                    max={addBounds.max}
                    placeholder="Set due date"
                    onChange={(value) => {
                      if (value && effectiveAddStatusId) {
                        handleSet(effectiveAddStatusId as Id<"fieldPilotStatuses">, value);
                        setAddStatusId("");
                      }
                    }}
                    aria-label="New milestone due date"
                  />
                </div>
              </div>
            ) : null}
          </div>

          {/* RIGHT: current milestones */}
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Milestones
            </p>
            {milestones.length === 0 ? (
              <p className="text-muted-foreground">
                {canEdit
                  ? "No milestone dates yet. Add one on the left to track this sub-task's stages."
                  : "No milestone dates yet."}
              </p>
            ) : (
              <div className="space-y-1.5">
                {milestones.map((entry) => {
                  const color = entry.colorHex ?? PIPELINE_FALLBACK_COLOR;
                  const bounds = milestoneDateBounds(datedForBounds, entry.rank);
                  return (
                    <div key={String(entry.statusId)} className="flex items-center gap-3">
                      <span className="flex w-[130px] shrink-0 items-center gap-2 font-medium">
                        <StatusDot colorHex={color} />
                        <span className="truncate" title={entry.name ?? "Status"}>
                          {entry.name ?? "Status"}
                        </span>
                      </span>
                      {canEdit ? (
                        <DatePicker
                          className="w-[150px]"
                          size="sm"
                          value={entry.dueDateKey ?? ""}
                          min={bounds.min}
                          max={bounds.max}
                          onChange={(value) => {
                            if (value) {
                              handleSet(entry.statusId, value);
                            } else {
                              handleRemove(entry.statusId);
                            }
                          }}
                          aria-label={`Due date for ${entry.name ?? "status"}`}
                        />
                      ) : (
                        <span className="w-[150px] shrink-0 tabular-nums">
                          {formatScheduleDate(entry.dueDateKey)}
                        </span>
                      )}
                      <span
                        className={cn(
                          "inline-flex items-center gap-1",
                          entry.reached ? "text-status-completed" : "text-muted-foreground"
                        )}
                      >
                        {entry.reached ? (
                          <>
                            <Check className="size-3.5" />
                            reached
                          </>
                        ) : (
                          <>
                            <span className="inline-block size-2.5 rounded-full border border-current" />
                            pending
                          </>
                        )}
                      </span>
                      <span className="tabular-nums text-muted-foreground">
                        {entry.atOrPastCount}/{entry.memberCount} items
                      </span>
                      {canEdit ? (
                        <IconButton
                          label={`Remove ${entry.name ?? "status"} milestone`}
                          icon={X}
                          size="xs"
                          iconClassName="size-3.5"
                          className="ms-auto shrink-0"
                          onClick={() => handleRemove(entry.statusId)}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * The date/percent cells every child row shares. Both layouts render the same five metrics; only
 * the progress bar's wrapper differs, so the row kinds cannot drift apart in formatting.
 */
function ChildMetricCells({
  metrics,
  leadingClassName,
  barCell,
}: {
  metrics: FieldPilotChildRowMetrics;
  leadingClassName?: string;
  barCell: React.ReactNode;
}) {
  return (
    <>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, leadingClassName, "whitespace-nowrap")}>
        {formatScheduleDate(metrics.startDateKey)}
      </TableCell>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "whitespace-nowrap")}>
        {formatScheduleDate(metrics.finishDateKey)}
      </TableCell>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "whitespace-nowrap")}>
        {formatDurationDays(metrics.durationDays)}
      </TableCell>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "whitespace-nowrap")}>
        {metrics.hasSchedule ? formatSchedulePercent(metrics.expectedFraction) : "—"}
      </TableCell>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "whitespace-nowrap font-semibold")}>
        {formatSchedulePercent(metrics.actualFraction)}
      </TableCell>
      {barCell}
    </>
  );
}

/** Item count spelled out, so a child's count can never be read as the task's measurement total. */
function formatChildItemCount(itemCount: number): string {
  return `${itemCount.toLocaleString()} item${itemCount === 1 ? "" : "s"}`;
}

/**
 * The task's ungrouped remainder, rendered as a peer of its sub-tasks. Read-only by nature: it
 * has no record behind it, so there is no toggle, no edit and no delete — the dashed badge says so.
 */
export function UngroupedRow({
  ungrouped,
  layout,
  onGroup,
}: {
  ungrouped: FieldPilotUngroupedRow;
  layout: SubTaskRowsLayout;
  /** Collect this remainder into a new sub-task. Omitted where grouping isn't offered. */
  onGroup?: () => void;
}) {
  const canEdit = useCanEdit();
  const rowClassName = fieldPilotRowClasses({
    kind: "ungrouped",
    isBehind: ungrouped.isBehind,
  });
  const nameCell = (
    <FieldPilotTreeCellContent kind="ungrouped">
      <div className="flex min-w-0 items-center gap-0.5">
        <span className="min-w-0 truncate text-sm font-medium text-muted-foreground">
          Not in a sub-task
        </span>
        {onGroup && canEdit ? (
          // Sits where a sub-task's edit control sits, so the row is actionable in the same
          // place — but it creates a sub-task rather than editing one, hence the wording.
          <IconButton
            label={`Group ${formatChildItemCount(ungrouped.itemCount)} into a sub-task`}
            tooltip="Group these items into a sub-task"
            icon={Pencil}
            size="xs"
            iconClassName="size-3.5"
            className="shrink-0"
            onClick={onGroup}
          />
        ) : null}
      </div>
    </FieldPilotTreeCellContent>
  );

  if (layout.variant === "schedule") {
    return (
      <TableRow className={rowClassName}>
        <TableCell className={cn(FIELD_PILOT_ROW_CELL, "text-muted-foreground")}>—</TableCell>
        <TableCell className={FIELD_PILOT_ROW_CELL}>
          <div className="flex items-center gap-2">
            {nameCell}
            <span className="shrink-0 text-xs text-muted-foreground">
              {formatChildItemCount(ungrouped.itemCount)}
            </span>
          </div>
        </TableCell>
        <ChildMetricCells
          metrics={ungrouped}
          barCell={
            <TableCell className={cn(FIELD_PILOT_ROW_CELL, "text-center")}>
              <div className="mx-auto w-20">
                <ScheduleProgressBar row={ungrouped} />
              </div>
            </TableCell>
          }
        />
        <TableCell className={FIELD_PILOT_ROW_CELL} />
      </TableRow>
    );
  }

  return (
    <TableRow className={rowClassName}>
      <TableCell className={FIELD_PILOT_ROW_CELL} />
      <TableCell className={FIELD_PILOT_ROW_CELL}>{nameCell}</TableCell>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "tabular-nums text-muted-foreground")}>
        {formatChildItemCount(ungrouped.itemCount)}
      </TableCell>
      {layout.showScheduleColumns ? (
        <ChildMetricCells
          metrics={ungrouped}
          leadingClassName={FIELD_PILOT_COLUMN_BAND}
          barCell={
            <TableCell className={cn(FIELD_PILOT_ROW_CELL, "min-w-[140px]")}>
              <ScheduleProgressBar row={ungrouped} />
            </TableCell>
          }
        />
      ) : null}
      <TableCell
        className={cn(FIELD_PILOT_ROW_CELL, FIELD_PILOT_COLUMN_BAND, "text-muted-foreground")}
      >
        {/* Stage is milestone-driven and this bucket has none. */}
        —
      </TableCell>
    </TableRow>
  );
}

/** A single sub-task rendered as a row aligned to its host table's columns, plus drill-down. */
function SubTaskRow({
  subTask,
  layout,
  colSpan,
  onEditSubTask,
  onDeleteSubTask,
}: {
  subTask: FieldPilotSubTaskRow;
  layout: SubTaskRowsLayout;
  colSpan: number;
  onEditSubTask: (subTaskId: Id<"fieldPilotSubTasks">) => void;
  onDeleteSubTask?: (subTaskId: Id<"fieldPilotSubTasks">) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const statusLine = subTask.statusLine ?? [];
  // Only stages past the starting stage (rank > 0) can carry milestones worth drilling into.
  const hasDrillDown = statusLine.some((entry) => entry.rank > 0);
  // Sub-tasks and item schedules are siblings and render identically; `isSolo` survives only
  // where the difference is real — the edit control's label, and child ordering.
  const rowClassName = fieldPilotRowClasses({
    kind: "sub-task",
    isBehind: subTask.isBehind,
  });
  const nameCell = (trailing?: React.ReactNode) => (
    <SubTaskNameCell
      subTask={subTask}
      onEditSubTask={onEditSubTask}
      isExpanded={expanded}
      onToggle={hasDrillDown ? () => setExpanded((current) => !current) : undefined}
      trailing={trailing}
    />
  );

  return (
    <>
      {layout.variant === "schedule" ? (
        <TableRow className={rowClassName}>
          <TableCell className={cn(FIELD_PILOT_ROW_CELL, "text-muted-foreground")}>—</TableCell>
          <TableCell className={FIELD_PILOT_ROW_CELL}>
            {nameCell(<CurrentStageCell subTask={subTask} />)}
          </TableCell>
          <ChildMetricCells
            metrics={subTask}
            barCell={
              <TableCell className={cn(FIELD_PILOT_ROW_CELL, "text-center")}>
                <div className="mx-auto w-20">
                  <ScheduleProgressBar row={subTask} />
                </div>
              </TableCell>
            }
          />
          <TableCell className={cn(FIELD_PILOT_ROW_CELL, "text-right")}>
            <DeleteEmptySubTaskButton subTask={subTask} onDeleteSubTask={onDeleteSubTask} />
          </TableCell>
        </TableRow>
      ) : (
        <TableRow className={rowClassName}>
          <TableCell className={FIELD_PILOT_ROW_CELL} />
          <TableCell className={FIELD_PILOT_ROW_CELL}>{nameCell()}</TableCell>
          {/* A sub-task's total is its item count, spelled out so it can't be read as the
              parent task's measurement total in the same column. */}
          <TableCell
            className={cn(FIELD_PILOT_ROW_CELL, "tabular-nums text-muted-foreground")}
          >
            {formatChildItemCount(subTask.itemCount)}
          </TableCell>
          {layout.showScheduleColumns ? (
            <ChildMetricCells
              metrics={subTask}
              leadingClassName={FIELD_PILOT_COLUMN_BAND}
              barCell={
                <TableCell className={cn(FIELD_PILOT_ROW_CELL, "min-w-[140px]")}>
                  <ScheduleProgressBar row={subTask} />
                </TableCell>
              }
            />
          ) : null}
          <TableCell className={cn(FIELD_PILOT_ROW_CELL, FIELD_PILOT_COLUMN_BAND)}>
            <div className="flex items-center justify-between gap-2">
              <CurrentStageCell subTask={subTask} />
              <DeleteEmptySubTaskButton subTask={subTask} onDeleteSubTask={onDeleteSubTask} />
            </div>
          </TableCell>
        </TableRow>
      )}
      {expanded && hasDrillDown ? (
        <MilestoneDrillDown subTask={subTask} colSpan={colSpan} />
      ) : null}
    </>
  );
}

/**
 * Nested sub-task detail rendered under an expanded tag row. Emits `<TableRow>`s directly so the
 * columns line up with the host table (the bottom progress table or the schedule dialog).
 */
export function SubTaskRows({
  subTasks,
  layout,
  onEditSubTask,
  onDeleteSubTask,
}: {
  subTasks: FieldPilotSubTaskRow[];
  layout: SubTaskRowsLayout;
  onEditSubTask: (subTaskId: Id<"fieldPilotSubTasks">) => void;
  onDeleteSubTask?: (subTaskId: Id<"fieldPilotSubTasks">) => void;
}) {
  const colSpan = getColumnCount(layout);
  if (subTasks.length === 0) {
    return (
      <TableRow className={fieldPilotRowClasses({ kind: "sub-task" })}>
        <TableCell
          colSpan={colSpan}
          className="py-2 pe-3 text-xs text-muted-foreground"
          style={{ paddingInlineStart: `${fieldPilotRowIndentRem("sub-task") + 1.5}rem` }}
        >
          No sub-tasks yet. Select measurements on the drawing and choose “Group into sub-task”.
        </TableCell>
      </TableRow>
    );
  }
  // Defensive sort so the hierarchy stays stable even if a query or local update returns rows
  // out of order.
  const orderedSubTasks = [...subTasks].sort((a, b) => a.displayOrder - b.displayOrder);
  return (
    <>
      {orderedSubTasks.map((subTask) => (
        <SubTaskRow
          key={String(subTask.subTaskId)}
          subTask={subTask}
          layout={layout}
          colSpan={colSpan}
          onEditSubTask={onEditSubTask}
          onDeleteSubTask={onDeleteSubTask}
        />
      ))}
    </>
  );
}
